import { H2, P, B, UL, LI, Callout, Table } from "./ui";

export const Nicho = () => (
  <>
    <P>
      Elegir nicho es la decisión que más determina si un canal funciona, y se toma antes de hacer
      el primer vídeo. Aquí hay además un criterio propio de esta herramienta que no aparece en las
      guías genéricas: <B>el nicho tiene que tener material visual</B>.
    </P>

    <H2 id="criterio-propio">El filtro que casi nadie aplica</H2>
    <P>
      Un documental necesita imágenes de lo que cuenta. Si el tema no tiene fotografía, metraje ni
      documentos de archivo, el vídeo acaba lleno de imágenes genéricas o de tarjetas de texto, y
      eso se nota y se abandona.
    </P>
    <Table
      head={["Nicho", "Material disponible"]}
      rows={[
        ["Historia del siglo XX", "Excelente: prensa, fotografía, cine de archivo"],
        ["Biografías de gente conocida", "Excelente: retratos, apariciones públicas"],
        ["Naufragios, catástrofes, sucesos", "Muy bueno: cobertura periodística"],
        ["Arte y arqueología", "Muy bueno: museos y colecciones abiertas"],
        ["Naturaleza y espacio", "Bueno: mucho metraje libre"],
        ["Crimen reciente", "Regular: material limitado y delicado"],
        ["Tecnología y empresas actuales", "Regular: mucho logo, poca imagen contable"],
        ["Sucesos de esta semana", "Malo: el archivo aún no existe"],
      ]}
    />

    <H2 id="otros">Los otros criterios</H2>
    <UL>
      <LI>
        <B>Que te aguante.</B> Vas a hacer decenas de vídeos del mismo tema. Si te aburre al quinto,
        se va a notar en el sexto.
      </LI>
      <LI>
        <B>Que tenga demanda demostrada.</B> Busca canales que ya vivan de ese nicho. Si no existe
        ninguno, lo más probable no es que hayas encontrado un hueco, sino que no hay público.
      </LI>
      <LI>
        <B>Que dé para muchos vídeos.</B> «La historia del Titanic» son tres vídeos; «naufragios
        del Atlántico Norte», cincuenta.
      </LI>
      <LI>
        <B>Que el público valga algo.</B> Los anunciantes pagan muy distinto según el tema y el
        país de la audiencia.
      </LI>
    </UL>

    <Callout tipo="truco" titulo="Sé estrecho al principio">
      Un canal nuevo se posiciona antes siendo específico. Es más fácil ser el canal de naufragios
      que el canal de historia: cuando ya tengas público, se amplía.
    </Callout>
  </>
);

export const Titulos = () => (
  <>
    <P>
      El título hace dos trabajos a la vez: convence al espectador de entrar y le dice al sistema
      qué vídeo tiene que hacer. Un título flojo estropea las dos cosas.
    </P>

    <H2 id="que-funciona">Lo que funciona</H2>
    <UL>
      <LI><B>Concreto antes que general.</B> Un nombre, una cifra, un lugar o una fecha.</LI>
      <LI><B>Una promesa clara.</B> Que se entienda qué vas a contar y por qué merece 15 minutos.</LI>
      <LI><B>Tensión sin engaño.</B> Prometer algo que el vídeo no cumple destruye la retención, y la retención es lo que decide si YouTube te recomienda.</LI>
      <LI><B>Listas, cuando encajan.</B> Anticipan estructura y prometen ritmo.</LI>
    </UL>

    <H2 id="ejemplos">Antes y después</H2>
    <Table
      head={["Flojo", "Mejor", "Por qué"]}
      rows={[
        ["La Segunda Guerra Mundial", "El error de un cabo que salvó a 300.000 soldados", "Concreto, con tensión y con archivo disponible"],
        ["Misterios del mar", "5 barcos que aparecieron vacíos y sin explicación", "Cifra, estructura y promesa clara"],
        ["Vida de Van Gogh", "Los 70 días en que Van Gogh pintó 75 cuadros", "Dato sorprendente y verificable"],
        ["Grandes inventos", "El invento que su creador intentó destruir", "Conflicto en seis palabras"],
      ]}
    />

    <Callout tipo="aviso" titulo="Cuidado con el clickbait">
      Un título que exagera trae clics y pierde espectadores a los treinta segundos. YouTube mide
      eso mejor que a ti, y el siguiente vídeo lo recomienda menos.
    </Callout>

    <H2 id="doble-uso">El título también busca el material</H2>
    <P>
      Del título salen los nombres, lugares y hechos que después se buscan en los archivos. «Grandes
      inventos» no tiene fotografías asociadas; «la máquina Enigma», muchísimas. Un título con
      nombres propios produce un vídeo con mejor material, sin que hagas nada más.
    </P>
  </>
);

export const Retencion = () => (
  <>
    <P>
      Que alguien entre depende del título y la miniatura. Que se quede depende del vídeo, y sobre
      todo de sus primeros treinta segundos.
    </P>

    <H2 id="apertura">La apertura</H2>
    <P>
      El guion abre con un gancho a propósito: una pregunta, una escena concreta o un dato que
      descoloca. Es la parte que más merece que revises y, si hace falta, reescribas a mano.
    </P>
    <UL>
      <LI><B>No empieces presentándote</B> ni explicando de qué va el canal. Nadie se queda por eso.</LI>
      <LI><B>Entra directo al hecho.</B> El contexto puede venir después, cuando ya hay interés.</LI>
      <LI><B>Que el material del gancho sea el mejor del vídeo</B>, no el que tocó.</LI>
    </UL>

    <H2 id="ritmo">El ritmo visual</H2>
    <P>
      Un documental de voz en off se hunde si la imagen se queda quieta. Por eso el montaje cambia
      de plano con frecuencia, mueve la cámara sobre las fotos y mete rótulos y transiciones. No
      tienes que configurarlo, pero sí conviene que lo mires en la revisión:
    </P>
    <UL>
      <LI>Escenas con tres o cuatro tarjetas de texto seguidas se ven pobres. Busca material real para esos tramos.</LI>
      <LI>El mismo clip repetido en escenas cercanas se nota mucho.</LI>
      <LI>Un plano que dura demasiado sin que pase nada pierde gente.</LI>
    </UL>

    <H2 id="duracion">La duración honesta</H2>
    <P>
      Alargar un vídeo para llegar a una cifra siempre se paga en retención. Si el tema da para
      doce minutos, hazlo de doce: un vídeo de doce que se ve entero rinde más que uno de veinte
      que se abandona en el ocho.
    </P>

    <Callout tipo="truco" titulo="Mira tus propias analíticas">
      Con el canal de YouTube conectado ves la retención de cada vídeo. El punto exacto donde cae
      te dice qué parte del guion no funcionó, y eso vale más que cualquier consejo general.
    </Callout>
  </>
);

export const Publicar = () => (
  <>
    <P>
      Producir es la mitad. La otra mitad es publicar con constancia y darle a YouTube tiempo para
      entender a quién enseñar tus vídeos.
    </P>

    <H2 id="constancia">La constancia gana a la intensidad</H2>
    <P>
      Un vídeo semanal durante seis meses funciona mejor que quince vídeos en un mes y luego nada.
      El calendario existe justo para eso: producir con ritmo sin depender de tu tiempo libre.
    </P>

    <H2 id="primeros">Los primeros vídeos no son el termómetro</H2>
    <P>
      Un canal nuevo tarda en encontrar público. Los primeros diez o quince vídeos sirven sobre todo
      para que tú aprendas qué títulos y qué temas funcionan en tu nicho, y para que YouTube empiece
      a entender a quién ofrecértelos.
    </P>

    <H2 id="mirar">Qué mirar de verdad</H2>
    <Table
      head={["Métrica", "Qué te dice"]}
      rows={[
        ["Porcentaje de clics (CTR)", "Si el título y la miniatura funcionan"],
        ["Retención media", "Si el vídeo cumple lo que prometía"],
        ["Punto de caída", "Qué parte concreta del guion falla"],
        ["Suscriptores por vídeo", "Si el canal tiene identidad reconocible"],
      ]}
    />
    <P>
      Las vistas totales son la métrica menos útil de todas: llegan tarde y no dicen qué corregir.
    </P>

    <Callout tipo="aviso" titulo="Revisa las licencias antes de monetizar">
      Los clips con aviso de licencia sin verificar pueden dar problemas en un canal monetizado.
      Sustituirlos cuesta muy poco; una reclamación cuesta bastante más.
    </Callout>
  </>
);
